/* eslint-disable eqeqeq */
import React, { useState, useEffect } from "react";
import "./producer-login.css";
import { useForm } from "react-hook-form";
import ProducerLoginFormData from "../../../services/producer-login/producer-login-data";
import { getErrorMessage } from "../../../utils/validations";
import Loader from "../../../utils/loader/loader";
import clock from "../../../assets/clock.svg";
import calendar from "../../../assets/calendar.svg";
import { formatDateTime, formatDuration } from "../../../utils/format";
import { useParams } from "react-router-dom";
import { ApiResponse, EventData } from "../../../interfaces/producer-login";
import ProducerDashboardView from "../dashboard/producer-dashboard-view";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import BrandLogo from "../../../components/brand-logo/BrandLogo";

const ProducerLoginForm = ({ eventData }: { eventData: EventData }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const [showMeetingSdk, setShowMeetingSdk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sdkData, setSdkData] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const [dateTime, setDateTime] = useState({
    formattedDate: "",
    formattedTime: "",
    timeZone: "",
  });
  const [rememberMe, setRememberMe] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (eventData.date && eventData.timeZone) {
      const formattedDateTime = formatDateTime(
        eventData.date,
        eventData.timeZone
      );
      setDateTime(formattedDateTime);
    }

    // Prepopulate form fields from localStorage if available
    const storedFormData = localStorage.getItem("producerLoginForm");
    if (storedFormData) {
      const parsedFormData = JSON.parse(storedFormData);
      setValue("firstName", parsedFormData.firstName);
      setValue("lastName", parsedFormData.lastName);
      setValue("jobTitle", parsedFormData.jobTitle);
      setValue("email", parsedFormData.email);
      setValue("password", parsedFormData.password);
      setValue("role", parsedFormData.role);
      setRememberMe(true);
    }
  }, [eventData.date, eventData.timeZone, setValue]);

  // Calculate event duration
  const eventDuration = formatDuration(eventData.duration);

  const onSubmit = async (formData: any) => {
    setLoading(true);

    // Handle Remember Me functionality
    if (rememberMe) {
      localStorage.setItem("producerLoginForm", JSON.stringify(formData));
    } else {
      localStorage.removeItem("producerLoginForm");
    }

    try {
      const numericEventId = Number(eventId);
      const response = (await ProducerLoginFormData(
        formData,
        numericEventId
      )) as ApiResponse;

      if (response && response.status === 200) {
        // Persist firstName/lastName from the submitted form so downstream consumers (e.g., Datadog)
        // can avoid re-parsing userName.
        setSdkData({
          ...response.data.data,
          firstName: formData.firstName,
          lastName: formData.lastName,
        });
        setShowMeetingSdk(true);
      } else if (response && response.status === 404) {
        toast.error(response.data.message);
      } else {
        toast.error("API call failed");
      }
    } catch (error) {
      toast.error("Form submission failed!");
    } finally {
      setLoading(false);
    }
  };

  const handleRememberMeChange = () => {
    setRememberMe(!rememberMe);
  };

  return (
    <div>
      {loading ? (
        <Loader />
      ) : !showMeetingSdk ? (
        <div className="container">
          <div className="row login-panel">
            <div className="col-md-12 login-form">
              <div className="col-md-6 content">
                <BrandLogo eventBrand={eventData?.eventBrand} />
                {/* <h1 className="main-heading">Welcome </h1> */}
                <h2 className="inner-heading-des">
                Welcome to the speaker<br/> login portal
                </h2>
                <h2 className="meeting-des">{eventData?.title}</h2>
                <div className="event-details">
                  <div className="time-wrapper">
                    <img
                      className="calendar"
                      src={calendar}
                      alt="loading-img"
                    />
                    <div className="inner-heading">
                      <i className="far fa-calendar-alt"></i>{" "}
                      {dateTime.formattedDate}
                    </div>
                  </div>
                  <div className="inner-heading-inner">
                    <i className="far fa-clock"></i> {dateTime.formattedTime}{" "}
                    {dateTime.timeZone == 'America/Detroit' ? 'America/New_York' : dateTime.timeZone}
                  </div>
                  <div className="time-wrapper">
                    <img className="clock" src={clock} alt="loading-img" />
                    <div className="inner-heading">
                      <i className="fas fa-hourglass-start"></i>
                      {eventDuration}
                    </div>
                  </div>
                </div>
                {/* <div className='info-wrapper'>
                                        Join us as we delve into the strategies financial institutions can employ to navigate and capitalize on these uncertainties.
                                    </div> */}
                {/* <div className='last-text-wrapper'>
                                        SPONSORED CONTENT FROM AMOUNT
                                    </div> */}
                <div className="event-description"></div>
              </div>
              <div className="col-md-6 content">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-group">
                    <input
                      type="text"
                      className={`form-control ${
                        errors.firstName ? "is-invalid" : ""
                      }`}
                      id="first-name"
                      placeholder="First Name"
                      {...register("firstName", {
                        required: "First Name is required",
                      })}
                      aria-invalid={errors.firstName ? "true" : "false"}
                    />
                    {errors.firstName && (
                      <div className="invalid-feedback">
                        {getErrorMessage(errors.firstName)}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      className={`form-control ${
                        errors.lastName ? "is-invalid" : ""
                      }`}
                      id="last-name"
                      placeholder="Last Name"
                      {...register("lastName", {
                        required: "Last Name is required",
                      })}
                      aria-invalid={errors.lastName ? "true" : "false"}
                    />
                    {errors.lastName && (
                      <div className="invalid-feedback">
                        {getErrorMessage(errors.lastName)}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      className={`form-control ${
                        errors.jobTitle ? "is-invalid" : ""
                      }`}
                      id="job-title"
                      placeholder="Job Title"
                      {...register("jobTitle", {
                        required: "Job Title is required",
                      })}
                      aria-invalid={errors.jobTitle ? "true" : "false"}
                    />
                    {errors.jobTitle && (
                      <div className="invalid-feedback">
                        {getErrorMessage(errors.jobTitle)}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      className={`form-control ${
                        errors.email ? "is-invalid" : ""
                      }`}
                      id="email"
                      placeholder="Email Address"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value:
                            /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                          message: "Enter a valid email address",
                        },
                      })}
                      aria-invalid={errors.email ? "true" : "false"}
                    />
                    {errors.email && (
                      <div className="invalid-feedback">
                        {getErrorMessage(errors.email)}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <div className="form-group">
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"} // Toggle between "text" and "password"
                          className={`form-control ${
                            errors.password ? "is-invalid" : ""
                          }`}
                          id="password"
                          placeholder="Password"
                          {...register("password", {
                            required: "Password is required",
                            minLength: {
                              value: 6,
                              message:
                                "Password must be at least 6 characters long",
                            },
                          })}
                          aria-invalid={errors.password ? "true" : "false"}
                        />
                        <div className="input-group-append">
                          <span
                            className="input-group-text"
                            onClick={togglePasswordVisibility}
                            style={{ cursor: "pointer" }}
                          >
                            <FontAwesomeIcon
                              icon={showPassword ? faEyeSlash : faEye}
                            />
                          </span>
                        </div>
                        {errors.password && (
                          <div className="invalid-feedback">
                            {getErrorMessage(errors.password)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <select
                      id="meeting-role"
                      className={`form-control ${
                        errors.role ? "is-invalid" : ""
                      }`}
                      {...register("role", {
                        required: "Meeting role is required",
                      })}
                      defaultValue="0" // Set Speaker as the default selected value
                      aria-invalid={errors.role ? "true" : "false"}
                    >
                      <option value="">Select your meeting role</option>
                      <option value="1">Host</option>
                      <option value="0">Speaker</option>
                    </select>
                    {errors.role && (
                      <div className="invalid-feedback">
                        {getErrorMessage(errors.role)}
                      </div>
                    )}
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                    />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                  </div>
                  <button type="submit" className="btn btn-primary">
                    Join
                  </button>
                  {/* <div className="last-section-heading">
                    <a href="#">System requirements</a> |{" "}
                    <a href="#">Security settings</a>
                  </div> */}
                </form>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ProducerDashboardView sdkData={sdkData} eventData={eventData} />
      )}
    </div>
  );
};

export default ProducerLoginForm;
